// Third Party
import * as React from 'react';

// Utils
import {
  PriceType,
  useComparisonQuoteByListingAndDatesLazyQuery,
} from '@gql';
import { ListingBySlugPartial } from '../utils';
import { useQuotes } from '../quotes-context';
import { ota_info, constructOtaUrl } from './utils';
import { useAnalytics } from '@contexts/use-analytics';
import { AnalyticsQuote, AnalyticsQuoteComparison } from '@contexts/use-analytics';
import {
  ComparisonQuoteResult,
  SuccessfulComparisonQuote,
  MissingComparisonQuote,
  isSuccessfulComparisonQuote,
} from './types';

type QuoteComparisonContext = {
  comparisonQuotes: ComparisonQuoteResult[];
  isLoading: boolean;
  showComparison: boolean;
  quoteUrls: Record<string, string>;
  afterTaxes: boolean;
};

const QuoteComparisonContext = React.createContext<QuoteComparisonContext>(undefined!);

type QuoteComparisonProviderProps = {
  children: React.ReactNode;
  listing: ListingBySlugPartial;
  afterTaxes?: boolean;
};

export const QuoteComparisonProvider: React.FC<QuoteComparisonProviderProps> = ({
  children,
  listing,
  afterTaxes = false,
}) => {
  const {
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
    quoteData,
  } = useQuotes();

  const { emitViewQuoteComparisonEvent } = useAnalytics();

  // Update ota_info URLs
  React.useEffect(() => {
    ota_info.airbnb.url = listing.airbnbUrl || undefined;
    ota_info.vrbo.url = listing.vrboUrl || undefined;
  }, [listing]);

  const [comparisonQuoteByListingAndDates] =
    useComparisonQuoteByListingAndDatesLazyQuery({});

  const [state, setState] = React.useState({
    comparisonQuotes: [] as ComparisonQuoteResult[],
    isLoading: true,
    showComparison: false,
  });

  const showPreTaxConditions =
    quoteData?.bookingQuoteByListingAndDates?.__typename === 'BookingQuote' &&
    quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents != null;

  React.useEffect(() => {
    let isSubscribed = true;
    let currentRequestId = 0;
    let timeoutRef: NodeJS.Timeout | undefined;

    const fetchComparisonQuotes = async () => {
      const thisRequestId = ++currentRequestId;

      // Clear any existing timeout
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        timeoutRef = undefined;
      }

      // Reset state immediately
      setState((prev) => ({ ...prev, isLoading: true, showComparison: false }));

      if (!checkInDate || !checkOutDate) {
        if (isSubscribed && thisRequestId === currentRequestId) {
          setState((prev) => ({
            ...prev,
            comparisonQuotes: [],
            isLoading: false,
            showComparison: false,
          }));
        }
        return;
      }

      const quotePromises = Object.values(ota_info)
        .filter((info) => info.url != null)
        .map(async ({ url, name }) => {
          if (url) {
            try {
              const response = await comparisonQuoteByListingAndDates({
                variables: {
                  externalListingUrl: url,
                  checkInDate,
                  checkOutDate,
                  numberOfAdults: adultCount,
                  numberOfChildren: childCount,
                  numberOfInfants: infantCount,
                  numberOfPets: petCount,
                  priceType: afterTaxes ? PriceType.TotalCost : PriceType.TotalWithoutTax,
                },
              });

              // Check for GraphQL errors
              if (response.error || response.data === undefined) {
                console.error(`GraphQL errors for ${name}:`, response.error);
                return null;
              }

              // Check for missing or invalid data
              if (!response.data?.comparisonQuoteByListingAndDates) {
                console.error(`No data returned for ${name}`);
                return null;
              }

              // Check for unexpected response type
              if (
                response.data.comparisonQuoteByListingAndDates.__typename !==
                'ComparisonQuote'
              ) {
                console.error(
                  `Unexpected response type for ${name}:`,
                  response.data.comparisonQuoteByListingAndDates
                );
                return null;
              }

              return response.data.comparisonQuoteByListingAndDates;
            } catch (error) {
              console.error(
                `Error fetching comparison quote for ${name}:`,
                error
              );
              return null;
            }
          }
          return null;
        });

      try {
        const settledComparisons = await Promise.allSettled(quotePromises);

        // If another request has started, abandon this one
        if (!isSubscribed || thisRequestId !== currentRequestId) return;

        const successfulComparisons: SuccessfulComparisonQuote[] = settledComparisons.reduce<
          SuccessfulComparisonQuote[]
        >((acc, result) => {
          if (
            result.status === 'fulfilled' &&
            result.value &&
            isSuccessfulComparisonQuote(result.value)
          ) {
            const comparisonQuote = result.value.priceCents;
            if (
              comparisonQuote &&
              quoteData?.bookingQuoteByListingAndDates &&
              quoteData?.bookingQuoteByListingAndDates.__typename ===
                'BookingQuote' &&
              quoteData?.bookingQuoteByListingAndDates.totalWithoutTaxesCents
            ) {
              acc.push(result.value as SuccessfulComparisonQuote);
            }
          }
          return acc;
        }, []);

        const foundOtaSources = new Set(
          successfulComparisons.map((quote) => quote.externalSource)
        );

        const completedComparisons: ComparisonQuoteResult[] = [
          ...successfulComparisons,
        ];
        Object.values(ota_info)
          .filter((info) => !foundOtaSources.has(info.name))
          .forEach((info) => {
            completedComparisons.push({
              externalSource: info.name,
            } as MissingComparisonQuote);
          });

        // Schedule the state update
        timeoutRef = setTimeout(() => {
          if (isSubscribed && thisRequestId === currentRequestId) {
            setState({
              comparisonQuotes: completedComparisons,
              isLoading: false,
              showComparison: showPreTaxConditions,
            });
          }
        }, 500);
      } catch (error) {
        console.error('Error fetching comparison quotes:', error);

        if (isSubscribed && thisRequestId === currentRequestId) {
          timeoutRef = setTimeout(() => {
            if (isSubscribed && thisRequestId === currentRequestId) {
              setState((prev) => ({
                ...prev,
                isLoading: false,
                showComparison: false,
              }));
            }
          }, 500);
        }
      }
    };

    fetchComparisonQuotes();

    return () => {
      isSubscribed = false;
      currentRequestId++; // Invalidate any in-flight request
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        timeoutRef = undefined;
      }
    };
  }, [
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
    quoteData,
    comparisonQuoteByListingAndDates,
  ]);

  // Calculate quote URLs for each comparison quote
  const quoteUrls = React.useMemo(() => {
    const urls: Record<string, string> = {};

    state.comparisonQuotes.forEach(quote => {
      if (quote.externalSource) {
        urls[quote.externalSource] = constructOtaUrl(quote, {
          checkInDate: checkInDate || undefined,
          checkOutDate: checkOutDate || undefined,
          adultCount,
          childCount,
          infantCount,
          otaInfo: ota_info,
        });
      }
    });

    return urls;
  }, [state.comparisonQuotes, checkInDate, checkOutDate, adultCount, childCount, infantCount]);

  // Emit analytics event when comparison data is ready
  React.useEffect(() => {
    if (
      !afterTaxes &&
      state.showComparison &&
      quoteData?.bookingQuoteByListingAndDates?.__typename === 'BookingQuote'
    ) {
      emitViewQuoteComparisonEvent(
        quoteData?.bookingQuoteByListingAndDates as AnalyticsQuote,
        state.comparisonQuotes as AnalyticsQuoteComparison[]
      );
    }
  }, [state, quoteData]);

  const contextValue: QuoteComparisonContext = {
    comparisonQuotes: state.comparisonQuotes,
    isLoading: state.isLoading,
    showComparison: state.showComparison,
    quoteUrls: quoteUrls,
    afterTaxes: afterTaxes,
  };

  return (
    <QuoteComparisonContext.Provider value={contextValue}>
      {children}
    </QuoteComparisonContext.Provider>
  );
};

export const useQuoteComparison = () => {
  const context = React.useContext(QuoteComparisonContext);

  if (context === undefined) {
    throw new Error('useQuoteComparison must be used within a QuoteComparisonProvider');
  }

  return context;
};
